import { STUDIO_VERSION, VITE_FORGE_URL } from '@constants';
import { Frame, Scene } from 'dragonmouse-shared/interfaces';
import { setLoadingState, updateGuestFrames, updateGuestScenes } from 'redux/db/actions';
import { setNewVersionAvailable } from 'redux/mode/actions';
import store from 'redux/store';
import { PollingStream } from './PollingStream';

interface SessionPollData {
	guestScenes: Scene[];
	guestFrames: Frame[];
	studioVersion?: string;
	timestamp: string | null;
}

const VERSION_DISMISS_KEY = 'versionBannerDismissed';
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
let versionChecked = false;
export let latestServerVersion = '';

function isNewerVersion(server: string, local: string): boolean {
	const [sMajor, sMinor] = server.split('.').map(Number);
	const [lMajor, lMinor] = local.split('.').map(Number);
	if (sMajor > lMajor) return true;
	if (sMajor === lMajor && sMinor > lMinor) return true;
	return false;
}

function isVersionDismissed(version: string): boolean {
	try {
		const raw = localStorage.getItem(VERSION_DISMISS_KEY);
		if (!raw) return false;
		const { version: dismissed, at } = JSON.parse(raw);
		if (dismissed !== version) return false;
		return Date.now() - at < TWO_DAYS_MS;
	} catch {
		return false;
	}
}

export function dismissVersionBanner(version: string) {
	localStorage.setItem(VERSION_DISMISS_KEY, JSON.stringify({ version, at: Date.now() }));
	store.dispatch(setNewVersionAvailable(false));
}

export class SessionPollingStream extends PollingStream<SessionPollData> {
	constructor() {
		const url = VITE_FORGE_URL + '/poll/session';

		const onError = (error: Error) => {
			console.error('Session polling stream error:', error);
			store.dispatch(updateGuestScenes([]));
			store.dispatch(updateGuestFrames([]));
		};

		super(url, onError);
	}

	extractData(response: any): SessionPollData[] {
		// Session endpoint returns an object with guestScenes and guestFrames
		if (!response) {
			return [];
		}
		return [response];
	}

	onData(sessionDataArray: SessionPollData[]) {
		if (sessionDataArray.length === 0) {
			store.dispatch(updateGuestScenes([]));
			store.dispatch(updateGuestFrames([]));
			store.dispatch(setLoadingState({ resource: 'guest.scenes', state: 'loaded' }));
			store.dispatch(setLoadingState({ resource: 'guest.frames', state: 'loaded' }));
			return;
		}

		const sessionData = sessionDataArray[0];

		// Check for newer studio version
		if (!versionChecked && sessionData.studioVersion) {
			versionChecked = true;
			if (
				isNewerVersion(sessionData.studioVersion, STUDIO_VERSION) &&
				!isVersionDismissed(sessionData.studioVersion)
			) {
				latestServerVersion = sessionData.studioVersion;
				store.dispatch(setNewVersionAvailable(true));
			}
		}

		// Update guest scenes
		const scenes: Scene[] = sessionData.guestScenes.map((scene) => ({
			id: scene.id,
			title: scene.title,
			metadata: scene.metadata,
			createdAt: new Date(scene.createdAt),
			updatedAt: scene.updatedAt ? new Date(scene.updatedAt) : undefined,
			writeId: scene.writeId,
			viewOnly: true,
		}));

		// Update guest frames
		const frames: Frame[] = sessionData.guestFrames.map((frame) => ({
			id: frame.id,
			sceneId: frame.sceneId,
			title: frame.title,
			x: frame.x,
			y: frame.y,
			width: frame.width,
			height: frame.height,
			imagePath: frame.imagePath,
			siteId: frame.siteId,
			entries: frame.entries,
			metadata: frame.metadata,
			createdAt: new Date(frame.createdAt),
			updatedAt: frame.updatedAt ? new Date(frame.updatedAt) : undefined,
			deletedAt: frame.deletedAt ? new Date(frame.deletedAt) : null,
			writeId: frame.writeId,
			viewOnly: true,
		}));

		store.dispatch(updateGuestScenes(scenes));
		store.dispatch(updateGuestFrames(frames));
		store.dispatch(setLoadingState({ resource: 'guest.scenes', state: 'loaded' }));
		store.dispatch(setLoadingState({ resource: 'guest.frames', state: 'loaded' }));
	}
}
